/**
 * Publik sahifalar uchun harakat (motion) yordamchilari.
 *
 * Barchasi bitta umumiy kuzatuvchiga tayanadi: `scroll`/`resize` hodisalari
 * rAF ichida bir marta yig'ilib qayta ishlanadi. IntersectionObserver ataylab
 * ishlatilmagan — u ba'zi muhitlarda (ko'rinmaydigan/offscreen kontekst,
 * o'rnatilgan brauzerlar) hech qachon ishga tushmaydi va kontent butunlay
 * yashiringancha qolib ketardi. Bu yerdagi tekshiruv esa har doim aniq
 * javob beradi.
 *
 * `prefers-reduced-motion: reduce` yoqilgan bo'lsa animatsiya umuman ishga
 * tushmaydi. JS ishlamasa ham kontent ochiq qoladi: yashirish
 * `<html class="has-reveal">` ga bog'langan, uni faqat shu modul qo'yadi.
 */
import { useEffect, useLayoutEffect, useState } from "react";
import type { RefObject } from "react";

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/* ------------------------------------------------------------ kuzatuvchi */

type Watcher = () => void;

const watchers = new Set<Watcher>();
let frame = 0;
let bound = false;

function runAll() {
  frame = 0;
  watchers.forEach((watcher) => watcher());
}

function schedule() {
  // Ko'rinmayotgan sahifada rAF chaqirilmaydi: navbatga qo'yilgan so'rov
  // osilib qoladi va keyingi barcha `schedule()` lar behuda ketardi.
  // Bunday holatda darhol, to'g'ridan-to'g'ri hisoblaymiz.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    runAll();
    return;
  }
  if (!frame) frame = window.requestAnimationFrame(runAll);
}

/** Funksiyani scroll/resize oqimiga ulaydi va darhol bir marta chaqiradi. */
export function watchViewport(fn: Watcher): () => void {
  if (!bound) {
    bound = true;
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // Sahifa fonda ochilgan bo'lsa rAF ishlamaydi va hisob-kitob eskirib
    // qoladi — ko'rinadigan holatga qaytganda qayta yurgiziladi.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") schedule();
    });
  }
  watchers.add(fn);
  fn();
  return () => {
    watchers.delete(fn);
  };
}

/** Element ko'rinish maydoniga (biroz pastki chekka bilan) kirdimi? */
export function isInViewport(el: Element, bottomFactor = 0.92): boolean {
  const box = el.getBoundingClientRect();
  if (box.width === 0 && box.height === 0) return false;
  return box.top < window.innerHeight * bottomFactor && box.bottom > 0;
}

/* ---------------------------------------------------------------- reveal */

/**
 * `[data-reveal]` elementlarini ko'rinish maydoniga kirganda ochadi.
 * Belgi ATRIBUT (`data-in`) orqali qo'yiladi, sinf orqali emas: `className`
 * ni React o'zi boshqaradi va qayta renderda (masalan CMS javobi kelganda)
 * qo'shilgan sinfni o'chirib yuborardi.
 *
 * `deps` — kontent asinxron kelganda yangi elementlarni ham qamrab olish uchun.
 */
export function useReveal(deps: unknown[] = []): void {
  // `useLayoutEffect` — yashirish sinfi birinchi bo'yashdan oldin qo'yiladi,
  // aks holda kontent bir kadr ko'rinib, keyin yo'qolib qayta chiqadi.
  useLayoutEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (!nodes.length) return;

    if (prefersReducedMotion()) {
      nodes.forEach((node) => node.setAttribute("data-in", ""));
      return;
    }

    document.documentElement.classList.add("has-reveal");

    let pending = nodes.filter((node) => !node.hasAttribute("data-in"));
    let unwatch = () => {};

    const sweep = () => {
      if (!pending.length) {
        unwatch();
        return;
      }
      pending = pending.filter((node) => {
        if (!isInViewport(node)) return true;
        node.setAttribute("data-in", "");
        return false;
      });
    };

    unwatch = watchViewport(sweep);
    return () => unwatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* ------------------------------------------------------- avtonom lenta */

interface AutoScrollOptions {
  /** Siljish tezligi, sekundiga piksel. */
  speed?: number;
  /** Tashqaridan to'xtatish (masalan «To'xtatish» tugmasi bosilganda). */
  paused?: boolean;
}

/**
 * Gorizontal lentani o'zi sekin siljitadi (avtonom scroll).
 *
 * Lenta ichidagi elementlar **ikki nusxada** bo'lishi kutiladi: birinchi
 * nusxaning oxiriga yetganda scroll bir aylanish kengligiga qaytariladi va
 * ko'z bilan seziladigan «sakrash» bo'lmaydi.
 *
 * To'xtatish holatlari: kursor lenta ustida, ichida fokus bor, foydalanuvchi
 * o'zi siljitdi (2,5 s), lenta ekrandan chiqib ketdi yoki `paused` berilgan.
 * `prefers-reduced-motion: reduce` yoqilgan bo'lsa harakat umuman ishga
 * tushmaydi — lenta oddiy qo'lda siljitiladigan blok bo'lib qoladi
 * (ЧТЗ 4.2.12, WCAG 2.3.3 va 2.2.2).
 */
export function useAutoScroll(
  ref: RefObject<HTMLElement | null>,
  { speed = 42, paused = false }: AutoScrollOptions = {},
): void {
  useEffect(() => {
    const node = ref.current;
    if (!node || paused || prefersReducedMotion()) return;

    let frame = 0;
    let previous = 0;
    /** Kursor ustida yoki ichida fokus bor. */
    let held = false;
    /** Qo'lda siljitilgandan keyingi qisqa pauza tugaydigan vaqt. */
    let holdUntil = 0;
    let visible = true;
    /** Bir aylanish kengligi — ikkinchi nusxaning boshigacha bo'lgan masofa. */
    let loopWidth = 0;
    /**
     * Joriy holat piksel kasri bilan shu yerda saqlanadi: `scrollLeft` ni har
     * kadrda qayta o'qish kasr qismini yo'qotadi va lenta deyarli qimirlamay
     * qoladi (kadriga 0,7 px → 0 ga yaxlitlanadi).
     */
    let position = node.scrollLeft;
    /** Qo'lda siljitilgandan keyin haqiqiy holatni qaytadan o'qish kerak. */
    let resync = false;

    const measure = () => {
      const items = node.children;
      const half = Math.floor(items.length / 2);
      const first = items[0] as HTMLElement | undefined;
      const middle = items[half] as HTMLElement | undefined;
      loopWidth = first && middle ? middle.offsetLeft - first.offsetLeft : 0;
      resync = true;
    };

    const hold = () => {
      holdUntil = performance.now() + 2500;
      resync = true;
    };
    /**
     * Lentani kimdir boshqa yo'l bilan siljitgan bo'lsa (scrollbar, `focus`
     * bilan ko'rinishga keltirish, boshqa skript) — haqiqiy holatdan davom
     * etamiz. O'zimiz yozgan qiymat `position` bilan bir xil bo'lgani uchun
     * bu tekshiruv o'z harakatimizni «begona» deb hisoblamaydi.
     */
    const syncOnScroll = () => {
      if (Math.abs(node.scrollLeft - position) > 2) resync = true;
    };
    // Faqat sichqoncha uchun: sensorli ekranda `pointerleave` kelmasligi mumkin
    // va lenta butunlay to'xtab qolardi.
    const enter = (event: PointerEvent) => {
      if (event.pointerType === "mouse") held = true;
    };
    const leave = (event: PointerEvent) => {
      if (event.pointerType === "mouse") held = false;
    };
    const focusIn = () => {
      held = true;
    };
    const focusOut = () => {
      held = false;
    };

    const step = (now: number) => {
      frame = window.requestAnimationFrame(step);
      // Fon tabidan qaytganda katta sakrash bo'lmasligi uchun qadam cheklanadi.
      const elapsed = previous ? Math.min(now - previous, 120) : 0;
      previous = now;
      if (held || !visible || now < holdUntil || loopWidth <= 0) return;
      if (resync) {
        position = node.scrollLeft;
        resync = false;
      }
      position += (speed * elapsed) / 1000;
      if (position >= loopWidth) position -= loopWidth;
      node.scrollLeft = position;
    };

    measure();
    const unwatch = watchViewport(() => {
      visible = isInViewport(node, 1);
    });
    window.addEventListener("resize", measure);
    node.addEventListener("pointerenter", enter);
    node.addEventListener("pointerleave", leave);
    node.addEventListener("focusin", focusIn);
    node.addEventListener("focusout", focusOut);
    node.addEventListener("wheel", hold, { passive: true });
    node.addEventListener("pointerdown", hold);
    node.addEventListener("keydown", hold);
    node.addEventListener("scroll", syncOnScroll, { passive: true });
    frame = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(frame);
      unwatch();
      window.removeEventListener("resize", measure);
      node.removeEventListener("pointerenter", enter);
      node.removeEventListener("pointerleave", leave);
      node.removeEventListener("focusin", focusIn);
      node.removeEventListener("focusout", focusOut);
      node.removeEventListener("wheel", hold);
      node.removeEventListener("pointerdown", hold);
      node.removeEventListener("keydown", hold);
      node.removeEventListener("scroll", syncOnScroll);
    };
  }, [ref, speed, paused]);
}

/* -------------------------------------------------------------- progress */

/** Sahifa bo'ylab o'qilgan ulush (0…1) — shapkadagi progress chizig'i uchun. */
export function useScrollProgress(): number {
  const [progress, setProgress] = useState(0);

  useEffect(
    () =>
      watchViewport(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(max > 8 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
      }),
    [],
  );

  return progress;
}

/* ---------------------------------------------------------------- spy */

/**
 * Ekranning o'rta chizig'idan yuqorida boshlangan oxirgi bo'lim id'sini
 * qaytaradi — menyudagi joriy bandni belgilash uchun (`.nav a.is-active`).
 */
export function useScrollSpy(ids: string[]): string {
  const key = ids.join("|");
  const [active, setActive] = useState("");

  useEffect(() => {
    const list = key.split("|").filter(Boolean);
    if (!list.length) return;

    return watchViewport(() => {
      const line = window.innerHeight * 0.42;
      let current = "";
      list.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const box = el.getBoundingClientRect();
        if (box.top <= line && box.bottom > line) current = id;
      });
      setActive(current);
    });
  }, [key]);

  return active;
}
